/**
 * Shared helpers for turning cookie-authed `/agents/files?key=...` URLs into
 * presigned S3 URLs.
 *
 * Two consumers need this, both because they hand ad content to something that
 * has no session cookie: the video-renderer iframe (`composition/`) and the
 * "Copy for Figma" clipboard payload (`figma-export/`).
 */

import type { TargetRenderer } from "@hightouch/lib/ad-studio/target-renderer";

const presignedUrlCache = new Map<string, string | Promise<string>>();

function cacheKeyFor(targetRenderer: TargetRenderer, s3Key: string): string {
  return `${targetRenderer}:${s3Key}`;
}

/**
 * CloudFront canned policies put unix-seconds in `Expires`. CloudFront custom
 * policies put them inside the base64 `Policy` document, at
 * `Statement[0].Condition.DateLessThan["AWS:EpochTime"]`. S3 SigV4 puts
 * `X-Amz-Date` + `X-Amz-Expires` (ttl seconds). Each form 403s once past
 * that instant; the iframe fetch has no cookie fallback.
 */
function signedUrlExpiryMs(url: string): number | undefined {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return undefined;
  }

  const expires = parsed.searchParams.get("Expires");
  if (expires !== null) {
    const seconds = Number(expires);
    if (Number.isFinite(seconds)) {
      return seconds * 1000;
    }
  }

  const policy = parsed.searchParams.get("Policy");
  if (policy !== null) {
    try {
      // @aws-sdk/cloudfront-signer writes base64 with `+/=` swapped for `-~_`.
      const policyDocument = JSON.parse(
        atob(policy.replace(/-/g, "+").replace(/~/g, "/").replace(/_/g, "=")),
      );
      const epochSeconds: unknown =
        policyDocument?.Statement?.[0]?.Condition?.DateLessThan?.[
          "AWS:EpochTime"
        ];
      if (typeof epochSeconds === "number" && Number.isFinite(epochSeconds)) {
        return epochSeconds * 1000;
      }
    } catch {
      return undefined;
    }
  }

  const amzDate = parsed.searchParams.get("X-Amz-Date");
  const amzExpires = parsed.searchParams.get("X-Amz-Expires");
  if (amzDate === null || amzExpires === null) {
    return undefined;
  }

  const match = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(amzDate);
  if (!match) {
    return undefined;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6]);
  const startMs = Date.UTC(year, month - 1, day, hour, minute, second);
  const ttlSeconds = Number(amzExpires);
  if (!Number.isFinite(startMs) || !Number.isFinite(ttlSeconds)) {
    return undefined;
  }
  return startMs + ttlSeconds * 1000;
}

export function isSignedUrlExpired(url: string, nowMs = Date.now()): boolean {
  const expiryMs = signedUrlExpiryMs(url);
  if (expiryMs === undefined) {
    return false;
  }
  return expiryMs <= nowMs;
}

/**
 * Presigned URLs are cached per S3 key. In-flight promises are cached too so
 * concurrent callers share one request; a rejection evicts the entry so the
 * next caller retries. A resolved URL past Expires / X-Amz-Expires is evicted
 * and re-signed. CloudFront and S3 both 403 after that window.
 */
export async function resolveS3Key({
  s3Key,
  apiEndpoint,
  targetRenderer,
}: {
  s3Key: string;
  apiEndpoint: string;
  targetRenderer: TargetRenderer;
}): Promise<string> {
  const cacheKey = cacheKeyFor(targetRenderer, s3Key);
  const cached = presignedUrlCache.get(cacheKey);
  if (cached !== undefined) {
    const url = await cached;
    if (!isSignedUrlExpired(url)) {
      return url;
    }
    if (presignedUrlCache.get(cacheKey) === cached) {
      presignedUrlCache.delete(cacheKey);
    } else {
      const inflight = presignedUrlCache.get(cacheKey);
      if (inflight !== undefined) {
        return inflight;
      }
    }
  }

  const promise = resolveS3KeyViaApi({ s3Key, apiEndpoint, targetRenderer });
  presignedUrlCache.set(cacheKey, promise);
  promise.catch(() => {
    if (presignedUrlCache.get(cacheKey) === promise) {
      presignedUrlCache.delete(cacheKey);
    }
  });
  return promise;
}

/**
 * The URL for `s3Key` when one is already in hand, for callers that resolve
 * refs synchronously while rendering. A fetch still in flight reads as a miss:
 * a promise can't be unwrapped without awaiting. An expired URL reads as a miss
 * too — the caller falls back to `/agents/files`, which re-signs.
 */
export function resolveS3KeyIfCached(s3Key: string): string | undefined {
  const cached = presignedUrlCache.get(cacheKeyFor("hightouch-webapp", s3Key));
  if (typeof cached !== "string" || isSignedUrlExpired(cached)) {
    return undefined;
  }
  return cached;
}

/**
 * No `targetRenderer` parameter: the server signs these URLs for the stored
 * object, which only the webapp renderer wants. Figma is served a converted
 * copy, so there is no figma-flavoured URL to prime.
 */
export function primeComponentAssetUrls({
  componentAssets,
  urls,
}: {
  componentAssets: Record<string, string> | null | undefined;
  urls: Record<string, string> | null | undefined;
}): void {
  if (!componentAssets || !urls) return;

  for (const [filename, s3Key] of Object.entries(componentAssets)) {
    // Filenames are agent- and user-supplied, so `urls[filename]` would resolve
    // to an Object.prototype member for names like `constructor`.
    if (!Object.hasOwn(urls, filename)) continue;
    // Indexing a Record is `string | undefined`, so this narrows rather than
    // validating: `Object.hasOwn` above already proved the key is there.
    const url = urls[filename];
    if (url === undefined) continue;
    // XXX: We currently can't use the presigned results of svg files because we
    // don't set the ContentType appropriately, so the browser won't render it.
    // Once we fix the content type on upload, we can remove this check.
    if (/\.svg$/i.test(s3Key)) continue;
    if (isSignedUrlExpired(url)) continue;

    const cacheKey = cacheKeyFor("hightouch-webapp", s3Key);
    if (!presignedUrlCache.has(cacheKey)) {
      presignedUrlCache.set(cacheKey, url);
    }
  }
}

async function resolveS3KeyViaApi({
  s3Key,
  apiEndpoint,
  targetRenderer,
}: {
  s3Key: string;
  apiEndpoint: string;
  targetRenderer: TargetRenderer;
}): Promise<string> {
  const params = new URLSearchParams({ key: s3Key, redirect: "false" });
  if (targetRenderer === "figma") params.set("targetRenderer", "figma");
  const response = await fetch(
    `${apiEndpoint}/agents/files?${params.toString()}`,
    { credentials: "include" },
  );
  if (!response.ok) {
    throw new Error(
      `Failed to resolve asset ${s3Key}: ${response.status} ${response.statusText}`,
    );
  }
  const data = (await response.json()) as { location?: string };
  if (!data.location) {
    throw new Error(`Asset response for ${s3Key} did not include a location`);
  }
  return data.location;
}

/**
 * Resolve every S3 key in `componentAssets` to a presigned URL, returning both
 * the filename-keyed map the renderer wants and the s3-key-keyed map
 * `rewriteAgentsFilesUrls` wants.
 */
export async function resolveComponentAssets({
  componentAssets,
  apiEndpoint,
  targetRenderer,
}: {
  componentAssets: Record<string, string> | null | undefined;
  apiEndpoint: string;
  targetRenderer: TargetRenderer;
}): Promise<{
  byFilename: Record<string, string>;
  byS3Key: Map<string, string>;
}> {
  const assets = componentAssets ?? {};
  const entries = await Promise.all(
    Object.entries(assets).map(async ([filename, s3Key]) => {
      const url = await resolveS3Key({
        s3Key,
        apiEndpoint,
        targetRenderer,
      });
      return [filename, s3Key, url] as const;
    }),
  );

  const byFilename = Object.fromEntries(
    entries.map(([filename, , url]) => [filename, url]),
  );
  const byS3Key = new Map(entries.map(([, s3Key, url]) => [s3Key, url]));
  return { byFilename, byS3Key };
}

const AGENTS_FILES_MARKER = "/agents/files?";

// Characters that terminate a URL inside an SVG attribute value — the same
// set that closes an XML attribute.
const URL_BOUNDARY_RE = /[\s"'<>]/;

/**
 * How `&` should be written in the replacement URL. Presigned URLs carry
 * multiple query params (`&Expires=`, `&Signature=`), and whether those
 * ampersands need escaping depends entirely on what the string is embedded in:
 *
 * - `"xml"` — the URL sits in an SVG/HTML attribute that will be re-parsed as
 *   markup, so `&` must be written `&amp;` or the parser eats the params as
 *   entity references.
 * - `"raw"` — the URL is a standalone JSON string value (a Doc layer's `src`).
 *   `&amp;` there would be taken literally and break the signature.
 *
 * There's no safe way to infer this from the matched text: a single-param URL
 * (`?key=abc`) contains no ampersand in either encoding, so it's
 * indistinguishable. The caller knows; make it say so.
 */
export type AmpersandEncoding = "xml" | "raw";

/**
 * Replace any `/agents/files?key=...` URLs in `content` with the corresponding
 * presigned URL from `s3KeyToPresigned`. URLs whose key isn't in the map are
 * left alone (and will fail to fetch wherever they land).
 * Handles absolute (`https://api.../agents/files?...`) and root-relative
 * (`/api/agents/files?...`) forms equally.
 *
 * The editor's `prepareSvg` family of helpers mutates SVG hrefs to
 * `/agents/files?key=...` URLs when an SVG layer enters the editor so the
 * parent's own DOM rendering of the SVG works against the cookie-authed
 * API. Those URLs survive into content we hand to cookie-less consumers, so we
 * rewrite at each hand-off boundary.
 *
 * Deliberately NOT a regex over the whole document: a pattern shaped like
 * `[^\s"'<>]*\/agents\/files\?[^\s"'<>]*` is O(n²) on long boundary-free
 * runs, and inline SVGs routinely embed multi-megabyte base64 `data:` URIs
 * — one such regex froze the editor's main thread for ~50s. Instead we
 * `indexOf` the literal marker (linear) and expand each hit outward to the
 * nearest boundary character.
 */
export function rewriteAgentsFilesUrls(
  content: string,
  s3KeyToPresigned: Map<string, string>,
  encoding: AmpersandEncoding,
): string {
  let idx = content.indexOf(AGENTS_FILES_MARKER);
  if (idx === -1) return content;

  const parts: string[] = [];
  let cursor = 0;
  while (idx !== -1) {
    let start = idx;
    while (start > cursor && !URL_BOUNDARY_RE.test(content.charAt(start - 1))) {
      start--;
    }
    let end = idx + AGENTS_FILES_MARKER.length;
    while (end < content.length && !URL_BOUNDARY_RE.test(content.charAt(end))) {
      end++;
    }
    parts.push(content.slice(cursor, start));
    parts.push(
      rewriteAgentsFilesUrl(
        content.slice(start, end),
        s3KeyToPresigned,
        encoding,
      ),
    );
    cursor = end;
    idx = content.indexOf(AGENTS_FILES_MARKER, cursor);
  }
  parts.push(content.slice(cursor));
  return parts.join("");
}

function rewriteAgentsFilesUrl(
  match: string,
  s3KeyToPresigned: Map<string, string>,
  encoding: AmpersandEncoding,
): string {
  try {
    // XML serializers encode `&` as `&amp;`. Decode so URLSearchParams
    // parses the `key` parameter correctly.
    const decoded = match.replace(/&amp;/g, "&");
    const url = new URL(decoded, "https://placeholder.invalid");
    const key = url.searchParams.get("key");
    if (!key) return match;
    const presigned = s3KeyToPresigned.get(key);
    if (!presigned) {
      console.warn(
        `[ad-studio] no presigned URL for /agents/files key "${key}"; leaving URL as-is`,
      );
      return match;
    }
    return encoding === "xml" ? presigned.replace(/&/g, "&amp;") : presigned;
  } catch {
    return match;
  }
}
